import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logoImg from '../../assets/logo.png';
import {
  Box,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
} from '@mui/material';
import { Google, ExpandMore, Fingerprint } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useAuthStore, type User } from '../../stores/auth.store';
import { authService } from '../../services/auth.service';
import {
  hasPasskeyOnThisDevice,
  isDismissal,
  passkeyAutofillSupported,
  passkeyService,
  passkeySupported,
} from '../../services/passkey.service';
import { WebAuthnAbortService } from '@simplewebauthn/browser';
import api, { ApiResponse } from '../../services/api';
import type { UserSettings } from '../../utils/user-settings';

interface LoginForm {
  email: string;
  password: string;
}

const DISMISSED_THIS_SESSION = 'mx-passkey-prompt-dismissed';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const rawReturnTo = searchParams.get('returnTo') || '';
  // Same-origin path only (the [/\\] guards open redirect once this drives a real navigation).
  const returnTo = /^\/(?![/\\])/.test(rawReturnTo) ? rawReturnTo : '';
  const goPostLogin = (replace = false) => {
    // returnTo is a server route (no SPA match) — needs a real navigation, not router navigate().
    if (returnTo) window.location.assign(returnTo);
    else navigate('/tracking', replace ? { replace: true } : undefined);
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmail, setShowEmail] = useState(true);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // Read once: it cannot change while the page is open.
  const [canUsePasskey] = useState(() => passkeySupported());

  useEffect(() => {
    const accessToken = searchParams.get('access_token');
    const oauthError = searchParams.get('error');

    if (oauthError === 'no_account') {
      const email = searchParams.get('email') || '';
      setError(`No account found for ${email}. Please contact your admin.`);
      return;
    }
    if (oauthError === 'account_inactive') {
      setError('Your account has been deactivated. Please contact your admin.');
      return;
    }

    if (accessToken) {
      setLoading(true);
      api.get<ApiResponse<{ id: string; email: string; first_name: string; last_name: string; role: User['role']; settings?: UserSettings; workspaces?: Array<{ id: string; name: string }> }>>('/auth/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((res) => {
          const data = res.data.data!;
          const user = {
            id: data.id,
            email: data.email,
            name: [data.first_name, data.last_name].filter(Boolean).join(' ') || data.email,
            role: data.role,
            workspaces: data.workspaces ?? [],
            settings: data.settings,
          };
          setAuth(user, accessToken);
          goPostLogin(true);
        })
        .catch(() => {
          setError('Failed to complete Google sign-in. Please try again.');
          setLoading(false);
        });
    }
  }, [searchParams, setAuth, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login(data);
      setAuth(response.user, response.accessToken);
      goPostLogin();
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const googleCallbackInFlight = Boolean(searchParams.get('access_token') || searchParams.get('error'));

  // With the hint, raise the sheet outright. Without it, ask conditionally, which
  // prompts only if a key really exists and is silent otherwise.
  useEffect(() => {
    if (!canUsePasskey) return undefined;
    // Google's callback owns the page while it finishes; a sheet on top would fight it.
    if (googleCallbackInFlight) return undefined;

    let live = true;
    const arrive = (session: { user: User; accessToken: string }) => {
      if (!live) return;
      setAuth(session.user, session.accessToken);
      goPostLogin(true);
    };

    (async () => {
      let dismissed = false;
      try {
        dismissed = sessionStorage.getItem(DISMISSED_THIS_SESSION) === '1';
      } catch { /* private window; treat as not dismissed */ }

      if (hasPasskeyOnThisDevice() && !dismissed) {
        try {
          arrive(await passkeyService.signIn());
          return;
        } catch (err) {
          if (isDismissal(err)) {
            try { sessionStorage.setItem(DISMISSED_THIS_SESSION, '1'); } catch { /* ignore */ }
          }
          return;
        }
      }

      if (!(await passkeyAutofillSupported())) return;
      try {
        arrive(await passkeyService.signIn({ autofill: true }));
      } catch {
        // Nothing offered, or a click started a ceremony that cancelled this one.
      }
    })();

    return () => {
      live = false;
      WebAuthnAbortService.cancelCeremony();
    };
    // Not searchParams: useSearchParams rebuilds it every render, and an effect
    // re-running that often would mint a challenge each time.
  }, [canUsePasskey, googleCallbackInFlight, setAuth, navigate]);

  const handlePasskeyLogin = async () => {
    setPasskeyBusy(true);
    setError(null);

    try {
      const { user, accessToken } = await passkeyService.signIn();
      setAuth(user, accessToken);
      goPostLogin();
    } catch (err) {
      if (!isDismissal(err)) {
        // Google will refuse a deactivated account too, so sending them there is a
        // second round of confusion rather than a way in.
        const reason = err instanceof Error ? err.message : '';
        setError(/not active/i.test(reason)
          ? 'Your account has been deactivated. Please contact your admin.'
          : 'That passkey was not accepted. Try Google or your password.');
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = '/api/v1/auth/google';
  };

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box
          component="img" src={logoImg} alt="Neptune"
          sx={{
            height: 44, mb: 0.5,
            filter: (t) => (t.palette.mode === 'dark' ? 'invert(1) hue-rotate(180deg)' : 'none'),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {canUsePasskey && (
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={!passkeyBusy && <Fingerprint />}
          onClick={handlePasskeyLogin}
          disabled={loading || passkeyBusy}
          sx={{ mb: 1.5 }}
        >
          {passkeyBusy ? <CircularProgress size={24} color="inherit" /> : 'Sign in with a passkey'}
        </Button>
      )}

      <Button
        fullWidth
        variant={canUsePasskey ? 'outlined' : 'contained'}
        size="large"
        startIcon={<Google />}
        onClick={handleGoogleLogin}
        sx={{ mb: 1.5 }}
      >
        Continue with Google
      </Button>

      <Button
        fullWidth
        size="small"
        onClick={() => setShowEmail(!showEmail)}
        endIcon={<ExpandMore sx={{ transform: showEmail ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
        sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
      >
        Sign in with email
      </Button>

      <Collapse in={showEmail}>
        <Divider sx={{ my: 1.5 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            autoComplete="username webauthn"
            size="small"
            margin="dense"
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Invalid email address',
              },
            })}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            autoComplete="current-password"
            size="small"
            margin="dense"
            error={!!errors.password}
            helperText={errors.password?.message}
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters',
              },
            })}
          />
          <Button
            fullWidth
            type="submit"
            variant="outlined"
            size="large"
            disabled={loading}
            sx={{ mt: 1.5 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
        </form>
      </Collapse>
    </Box>
  );
}
